// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Box, Flex, useMobileMediaQuery } from '@iheartjane/reefer';
import sortBy from 'lodash-es/sortBy';
import { useParams } from 'react-router';

import { useMenu } from '@jane/shared-ecomm/providers';
import type { CarouselBanner } from '@jane/shared/components';
import { Carousel } from '@jane/shared/components';
/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';
import type { MenuCategoryPath } from '@jane/shared/types';

import { useGetPage } from '../../hooks/useGetPage';
import { MenuCommsBanner } from './MenuCommsBanner/MenuCommsBanner';
import { MenuReviewBanner } from './MenuReviewBanner/MenuReviewBanner';

const StyledFlex = styled(Flex)({
  '&:empty': {
    padding: 0,
  },
});

const getMobileCarouselSlides = (
  mobileCarouselSlides: CarouselBanner[],
  desktopCarouselSlides: CarouselBanner[]
) => {
  if (!mobileCarouselSlides) {
    return desktopCarouselSlides;
  }

  const missingSlides = desktopCarouselSlides.filter(
    (slideA) =>
      !mobileCarouselSlides.some(
        (slideB) => slideB.position === slideA.position
      )
  );

  return sortBy([...missingSlides, ...mobileCarouselSlides], 'position');
};

const storeHasVisibleCarousel = (store: Store) =>
  store?.carousel_banner &&
  store?.carousel_banner.length > 0 &&
  store?.carousel_banner.every((slide) => slide.visible);

export const MenuBanners = ({ tabs }: { tabs: MenuCategoryPath[] }) => {
  const {
    appInfo: { appMode },
    store,
  } = useMenu();
  const params = useParams();
  const page = useGetPage();
  const firstTab = tabs[0];
  const isMobile = useMobileMediaQuery({});

  const showCommsBanner =
    page === 'menu' ||
    page === firstTab ||
    (page === 'category' && params['category'] === firstTab);

  const commsBannerEnabled = [
    'embedded',
    'framelessEmbed',
    'whiteLabel',
    'operatorEmbed',
  ].includes(appMode);

  const reviewBannerEnabled = [
    'default',
    'embedded',
    'framelessEmbed',
    'whiteLabel',
  ].includes(appMode);

  if (!commsBannerEnabled && !reviewBannerEnabled) return null;

  const desktopCarouselSlides =
    store?.carousel_banner?.filter((i) => i.breakpoint === 'desktop') || [];

  const mobileCarouselSlides =
    store?.carousel_banner?.filter((i) => i.breakpoint === 'mobile') || [];

  return (
    <StyledFlex gap={24} flexDirection="column" pb={24}>
      {commsBannerEnabled && showCommsBanner && <MenuCommsBanner />}
      {reviewBannerEnabled && <MenuReviewBanner />}
      {appMode === 'framelessEmbed' && storeHasVisibleCarousel(store) && (
        <Box px={isMobile ? 24 : 64}>
          {isMobile ? (
            <Carousel
              key="mobile"
              items={getMobileCarouselSlides(
                mobileCarouselSlides,
                desktopCarouselSlides
              )}
              storeName={store?.name}
            />
          ) : (
            <Carousel
              key="desktop"
              items={desktopCarouselSlides}
              storeName={store?.name}
            />
          )}
        </Box>
      )}
    </StyledFlex>
  );
};
